import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { AssetManager } from '../assets/AssetManager';
import { LightingSystem } from '../lighting/LightingSystem';
import { EnvironmentSystem } from '../environment/EnvironmentSystem';
import { CameraManager } from '../camera/CameraManager';
import { ColorGradeShader, DEFAULT_COLOR_GRADE, type ColorGradeConfig } from './colorGrade';

export type UpdateCallback = (deltaSeconds: number, elapsedSeconds: number) => void;

/**
 * Engine's job is narrow and deliberately stays that way: rendering, scene
 * management, asset loading, lighting, and the update loop. It knows
 * nothing about golf — no Course, no Hole, no fairway/rough/bunker. Course
 * (see src/course/) consumes the services exposed here; Engine never
 * imports anything from src/course/ or src/rendering/ (the course-data-
 * consuming rendering layer) — post-processing/color-grading below is
 * Engine-level infrastructure, not a golf-specific concern, which is why
 * `core/colorGrade.ts` lives alongside Engine rather than in `rendering/`.
 *
 * Engine also no longer owns a single permanent camera — see CameraManager.
 *
 * Rendering goes through an EffectComposer (RenderPass -> color grade ->
 * OutputPass) rather than a direct renderer.render() call, so tonemapping/
 * color-space conversion happens exactly once, at OutputPass, on the
 * final screen write — three only applies those when the current render
 * target is the screen (verified directly in WebGLPrograms.js), so the
 * intermediate grading pass operates on linear HDR data, not
 * already-tonemapped output.
 */
export class Engine {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene: THREE.Scene;
  readonly clock: THREE.Clock;

  readonly assets: AssetManager;
  readonly lighting: LightingSystem;
  readonly environment: EnvironmentSystem;
  readonly cameras: CameraManager;

  private readonly composer: EffectComposer;
  private readonly renderPass: RenderPass;
  private readonly colorGradePass: ShaderPass;

  private readonly canvas: HTMLCanvasElement;
  private readonly updateCallbacks: UpdateCallback[] = [];
  private frameHandle = 0;

  constructor(container: HTMLElement) {
    this.canvas = document.createElement('canvas');
    container.appendChild(this.canvas);

    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    // Physically based color pipeline: sRGB output + filmic tonemapping so
    // PBR material response and HDR environment lighting look correct.
    // Only applied at the final screen write (see OutputPass below), not
    // to the composer's intermediate linear HDR buffers.
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = DEFAULT_COLOR_GRADE.exposure;

    this.scene = new THREE.Scene();
    this.clock = new THREE.Clock();

    this.assets = new AssetManager();
    this.lighting = new LightingSystem(this.scene);
    this.environment = new EnvironmentSystem(this.renderer, this.scene);
    this.cameras = new CameraManager();

    this.composer = new EffectComposer(this.renderer);
    this.renderPass = new RenderPass(this.scene, new THREE.PerspectiveCamera());
    this.colorGradePass = new ShaderPass(ColorGradeShader);
    const outputPass = new OutputPass();
    this.composer.addPass(this.renderPass);
    this.composer.addPass(this.colorGradePass);
    this.composer.addPass(outputPass);

    window.addEventListener('resize', this.handleResize);
    this.handleResize();
  }

  private handleResize = (): void => {
    const { clientWidth: width, clientHeight: height } = this.canvas.parentElement ?? document.body;
    const aspect = width / Math.max(1, height);
    this.cameras.handleResize(aspect);
    this.renderer.setSize(width, height, false);
    this.composer.setSize(width, height);
  };

  /** Applies exposure/contrast/saturation grading. Exposure maps to renderer.toneMappingExposure (applied at OutputPass); contrast/saturation run in the dedicated grading pass. */
  applyColorGrade(config: ColorGradeConfig): void {
    this.renderer.toneMappingExposure = config.exposure;
    this.colorGradePass.uniforms.contrast.value = config.contrast;
    this.colorGradePass.uniforms.saturation.value = config.saturation;
  }

  /** Register a per-frame callback, called before rendering. Returns a function to unregister it. */
  onUpdate(callback: UpdateCallback): () => void {
    this.updateCallbacks.push(callback);
    return () => {
      const index = this.updateCallbacks.indexOf(callback);
      if (index !== -1) this.updateCallbacks.splice(index, 1);
    };
  }

  start(): void {
    const tick = (): void => {
      const delta = this.clock.getDelta();
      const elapsed = this.clock.getElapsedTime();
      for (const cb of this.updateCallbacks) cb(delta, elapsed);
      this.cameras.update(delta);
      this.renderPass.camera = this.cameras.activeCamera;
      this.composer.render(delta);
      this.frameHandle = requestAnimationFrame(tick);
    };
    this.frameHandle = requestAnimationFrame(tick);
  }

  dispose(): void {
    cancelAnimationFrame(this.frameHandle);
    window.removeEventListener('resize', this.handleResize);
    this.cameras.dispose();
    this.lighting.dispose();
    this.environment.dispose();
    this.composer.dispose();
    this.renderer.dispose();
    this.canvas.remove();
  }
}
